import * as stylex from '@stylexjs/stylex'
import { Children, cloneElement, isValidElement, type ReactElement, type ElementType } from 'react'
import { Button, type ButtonProps, type Size } from '../Button/Button'
import { type SvgIconProps } from '../SvgIcon/SvgIcon'
import { type AnyComponent } from '../types/UtilityTypes'

const sizeVariants = stylex.create({
  small: (hasBorder: boolean) => ({
    padding: hasBorder ? 5 : 6,
  }),
  medium: (hasBorder: boolean) => ({
    padding: hasBorder ? 7 : 8,
  }),
  large: (hasBorder: boolean) => ({
    padding: hasBorder ? 9 : 10,
  }),
  extraLarge: (hasBorder: boolean) => ({
    padding: hasBorder ? 11 : 12,
  }),
})

const iconButtonVariants = stylex.create({
  text: {
    padding: 0,
  },
  filled: {},
  outlined: {},
  ghost: {},
  circle: {},
})

const baseStyles = stylex.create({
  root: {
    alignItems: 'center',
    fontSize: 'inherit',
    justifyContent: 'center',
    minWidth: 'auto',
  },
})

export type IconButtonProps<RootComponent extends ElementType = 'button', BoxElement extends HTMLElement = HTMLButtonElement> = {
  /**
   * 內部圖示的字體大小（px）
   * @default 20
   */
  fontSize?: SvgIconProps['fontSize']
  /**
   * 按鈕尺寸
   * @default 'medium'
   */
  size?: Size
} & ButtonProps<RootComponent, BoxElement>

/**
 * IconButton 圖示按鈕，僅顯示圖示而無文字標籤。
 * 基於 Button 實作，會自動將子元素中的 SvgIcon 設定 fontSize。
 */
export const IconButton = <RootComponent extends ElementType = 'button', BoxElement extends HTMLElement = HTMLButtonElement>(props: IconButtonProps<RootComponent, BoxElement>) => {
  const { component = 'button', children, fontSize = 20, loading, variant = 'text', color = 'grey', rootStyle, size = 'medium', ...rest } = props
  const hasBorder = variant === 'outlined'
  const elements = Children.toArray(children)

  return (
    <Button
      component={component}
      rootStyle={[
        baseStyles.root,
        sizeVariants[size as keyof typeof sizeVariants](hasBorder),
        iconButtonVariants[variant],
        rootStyle,
      ]}
      loading={loading}
      variant={variant}
      color={color}
      {...rest}
    >
      {
        elements.map((element) => {
          if (
            isValidElement(element)
            && typeof element.type !== 'string'
            && (element.type as AnyComponent).displayName === 'CreateSvgIcon'
          ) {
            return cloneElement(element as ReactElement<{ fontSize: number | string }>, { fontSize })
          }
          return element
        })
      }
    </Button>
  )
}
